import { Container, inject, injectable } from "inversify";
import IAjaxConnect from "../connect/ajax/IAjaxConnect";
import { FETCH_TYPES, parseURL } from "../connect/Connect";
import FetchAjax from "../connect/FetchAjax";
import SystemConfig, { CONFIG_PARAMS } from "../system/SystemConfig";
import SERVICE_IDENTIFIER from "../constants/identifiers";
import { wait } from "../helpers";
import { errorFactroy } from "../error/MadmaxError";
import AjaxLoading from "../ui/components/AjaxLoading";
import AjaxLoadingNew from "../ui/components/AjaxLoadingNew";
import MadmaxMessage from "../ui/messages/MadmaxMessage";
import { MESSAGE_EVENTS, MESSAGE_TYPES } from "../error/errorMessages";
import { translate } from "../translations/translations";
import { Method } from "axios";
import { importAll } from "../modules/Utils";
import { ERROR_CODES } from "../error/errorCodes";
@injectable()
class Settings implements IAjaxConnect {
	public readonly fetchType: string = FETCH_TYPES.SETTINGS;
	timeout: number = 15000;
	method: Method = "get";
	attempts: number = 3;
	loadingTimer: number;
	loadingTimeout: number = 4000;

	private _data: any;
	private fetchModule: FetchAjax;
	private config: SystemConfig;
	private loading: AjaxLoading | AjaxLoadingNew;
	private _behavior: any = null;

	constructor(
		@inject(SERVICE_IDENTIFIER.SystemConfig) config: SystemConfig,
		@inject(SERVICE_IDENTIFIER.FetchAjax) fetch: FetchAjax
	) {
		this.fetchModule = fetch;
		this.config = config;
		this._data = {};

		const isBellot = this.config.get(CONFIG_PARAMS.BRAND_NAME) === 'bellot';

		this.loading = isBellot ? new AjaxLoadingNew() : new AjaxLoading();
		this.loading.create(document.body);
		// this.loading.showLoading();
	}

	async init(container: Container) {
		let res;
		try {
			this.initTimeoutLoading();

			res = await this.fetch({});

			this._data = res.data;
			this.stopTimeoutLoading();
		} catch (error) {
			console.error(error);

			this.showErrorMessage();
			return Promise.reject();
		} finally {
			this.loading.destroy();
		}
	}

	async fetch(data?: {}): Promise<any> {
		let lastFetchTime = Date.now();
		let res;
		try {
			res = await this.fetchModule.fetch({
				url: this.getUrl(),
				params: {},
				timeout: this.timeout,
				method: this.method,
			});
		} catch (error) {
			console.error(error);
			try {
				let madmaxError = errorFactroy({ error, fetchType: this.fetchType });
				if (!!madmaxError.isNetworkError() || --this.attempts < 1) {
					throw madmaxError;
				}
			} catch (madmaxError) {
				return await Promise.reject(madmaxError);
			}

			let delay = this.getDelay(this.timeout, lastFetchTime);

			if (delay) await wait(delay);

			return await this.fetch();
		}
		return res;
	}

	getDelay(timeout, lastFetchTime = null) {
		if (lastFetchTime == null) {
			return 0;
		}

		let delay = timeout - (Date.now() - lastFetchTime);
		if (delay < 0) delay = 0;

		return delay;
	}

	getUrl() {
		let url = parseURL(this.config.get(CONFIG_PARAMS.URL_SETTINGS), {
			server_api: this.config.get(CONFIG_PARAMS.SERVER_API),
			casino_id: this.config.get(CONFIG_PARAMS.CASINO_ID),
			game_id: this.config.get(CONFIG_PARAMS.GAME_ID),
			currency: this.config.get(CONFIG_PARAMS.CURRENCY),
		});
		if (this.config.get(CONFIG_PARAMS.PLAYER_ID) !== null) {
			url += "&player_id=" + this.config.get(CONFIG_PARAMS.PLAYER_ID);
		}
		if (this.config.get(CONFIG_PARAMS.MAX_ALLOWED_BET_URL) !== null) {
			url += "&max_allowed_bet=" + this.config.get(CONFIG_PARAMS.MAX_ALLOWED_BET_URL)
		}
		return url;
	}

	getData() {
		return this._data;
	}

	getLegislation() {
		return this._data.legislation;
	}

	getKeyBindings() {
		return this._data.key_bindings;
	}

	getBrandName(){
		return this._data.brand_name;
	}

	getBehaviorPattern() {
		return this._data.behavior_pattern;
	}

	getWebSocket() {
		return this._data.web_socket;
	}

	getCoins(): Array<number> {
		return this._data.logic?.coins;
	}

	getFsCoins(): number[] | undefined {
		return this._data.logic?.fs_coins;
	}

	getLinesCount() {
		return this._data.lines_count;
	}

	getCoin(): number {
		return this._data.coin;
	}

	getBet(): number {
		return this._data.bet;
	}

	getTargetTotalBet(): number {
		return this._data.target_total_bet;
	}

	getDecimalPlaces() {
		return this._data.decimal_places;
	}

	getBetSet() {
		return this._data.logic?.bet_set;
	}

	getFSBetSet(): number[] {
		return this._data.logic?.fs_bet_set;
	}

	getLines() {
		return this._data.logic?.line_set;
	}

	getMinBigWinFactor() {
		return this.getBehavior()?.reel?.minBigWinFactor;
	}

	getMinMegaWinFactor() {
		return this.getBehavior()?.reel?.minMegaWinFactor;
	}

	getMinSuperMegaWinFactor() {
		return this.getBehavior()?.reel?.minSuperMegaWinFactor;
	}

	getMinEpicWinFactor() {
		return this.getBehavior()?.reel?.minEpicWinFactor;
	}

	getMaxWinFactor() {
		return this.getBehavior()?.reel?.maxWinFactor;
	}

	getSoundBehavConfig() {
		return this.getBehavior()?.sound;
	}

	getBehavior() {
		if (this._behavior !== null) return this._behavior;

		if (this.getBehaviorPattern()) {
			const conf = this.retrieveConfigs();
			if (this._data.behavior?.is_personal)
				return (this._behavior = this._data.behavior);
			if (conf[this.getBehaviorPattern()])
				return (this._behavior = conf[this.getBehaviorPattern()]);
			if (this._data.behavior) return (this._behavior = this._data.behavior);
			if (conf["default"]) return (this._behavior = conf["default"]);
			if (conf) return (this._behavior = conf[Object.keys(conf)[0]]);
			return null;
		} else {
			return (this._behavior = this._data.behavior);
		}
	}

	isForcestopEnabled() {
		return this._data.force_stop;
	}

	getTimeouts() {
		return this._data.timeouts;
	}

	getUseFSTotalWin(){
		return this._data.use_fs_total_win ?? false
	}

	getHistory() {
		return this._data.history2 ?? this._data.history;
	}

	getParagraph() {
		return this._data.pages2 ?? this._data.paragraph;
	}

	getWarningMessage() {
		return this._data.warning_message;
	}

	getFullScreen() {
		return this._data.full_screen;
	}

	getResponsibleGaming() {
		return this._data.responsible_gaming;
	}

	getPlayerOptions() {
		return this._data.player_options;
	}

	getActions() {
		return this._data.actions;
	}

	getCloseSessions() {
		return this._data.close_sessions;
	}

	getUIMode() {
		return this._data.ui_mode;
	}

	getAutoStart() {
		return this._data.auto_start;
	}

	getDisableAutostart(): boolean {
		return !this._data.auto_start?.is_enabled;
	}

	getMaxBetWarning(): { level?: number, timeout?: number } | undefined {
		return this._data.max_bet_warning
	}

	getCreditBets() {
		return this._data.bet_values;
	}
	getFsCreditBets() {
		return this._data.fs_bet_values;
	}

	getCDN() {
		return this._data.cdn;
	}

	getMaxBet() {
		return this._data.max_bet;
	}

	getMaxLineBet() {
		return this._data.max_line_bet;
	}

	getMinBet() {
		return this._data.min_bet;
	}

	getFsMinBet(): number {
		return this._data.logic?.fs_min_bet;
	}

	getMinLineBet() {
		return this._data.min_line_bet;
	}

	getCreditOrder(): string {
		return this._data.credit_order;
	}

	getPlatform(): string {
		return this._data.platform;
	}

	getHomeSettings(): object {
		return this._data.home;
	}

	getPostMessages(): boolean {
		return this._data.post_messages || false;
	}

	getJokerBonus(): boolean {
		return this._data.joker_bonus ?? false;
	}
	getJokerBonusMode(): string {
		return this._data.joker_bonus_mode;
	}
	getJokerBonusPopup(): string {
		return this._data.joker_bonus_popup_notification;
	}
	getRtp(): number | null {
		return this._data.rtp ?? null;
	}

	getAppsButtons(): any {
		return this._data.app_buttons ?? {};
	}

	getUseWSBalance(): boolean {
		return this._data.use_ws_balance;
	}

	getDynamicUrls() {
		return this._data?.dynamic_urls;
	}

	getSkipIdOnError(): null | number[] | 'all' {
		return this._data.skip_id_on_error ?? null;
	}

	retrieveConfigs() {
		// @ts-ignore
		return importAll(require.context("/config", true, /\.(json)$/));
	}

	protected initTimeoutLoading() {
		this.loadingTimer = setTimeout(() => {
			if (this.config.get(CONFIG_PARAMS.BG_COLOR) !== "transparent") {
				document.body.style.backgroundColor = this.config.get(
					CONFIG_PARAMS.BG_COLOR
				);
			}
			this.loading.showLoading();
		}, this.loadingTimeout);
	}

	protected stopTimeoutLoading() {
		clearTimeout(this.loadingTimer);
		this.loading.hideLoading();
	}

	showErrorMessage() {
		let msg = new MadmaxMessage(
			"",
			translate("service_unavailable", this.config.get(CONFIG_PARAMS.LANGUAGE)),
			{ Close: { event: MESSAGE_EVENTS.MESSAGE_CLOSE_GAME } },
			this.config.get(CONFIG_PARAMS.LANGUAGE),
			MESSAGE_TYPES.ERROR,
			100,
			this.config,
			ERROR_CODES.SETTINGS_ERROR
		);
		msg.create();
		msg.openMessage();
		document.body.append(msg.getElement());
	}
}

export default Settings;
